<template>
  <div class="p-6 bg-white rounded-lg shadow-md">
    <h1 class="text-3xl font-bold mb-6 text-gray-800">Gestion des Entreprises</h1>

    <!-- Formulaire d'ajout/édition d'entreprise -->
    <div class="mb-8 p-4 border border-gray-200 rounded-lg">
      <h2 class="text-xl font-semibold mb-4 text-gray-700">
        {{ editingCompany.id ? 'Modifier l\'Entreprise' : 'Ajouter une Nouvelle Entreprise' }}
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label for="companyName" class="block text-sm font-medium text-gray-700">Nom de l\'Entreprise</label>
          <input
            type="text"
            id="companyName"
            v-model="editingCompany.name"
            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
          />
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-2">Modules Activés</label>
          <div class="flex flex-wrap gap-4">
            <div class="flex items-center">
              <input type="checkbox" id="visitorsModule" v-model="editingCompany.modules.visitors" class="h-4 w-4 text-orange-600 border-gray-300 rounded" />
              <label for="visitorsModule" class="ml-2 block text-sm text-gray-900">Visiteurs</label>
            </div>
            <div class="flex items-center">
              <input type="checkbox" id="visitorVehiclesModule" v-model="editingCompany.modules.visitor_vehicles" class="h-4 w-4 text-orange-600 border-gray-300 rounded" />
              <label for="visitorVehiclesModule" class="ml-2 block text-sm text-gray-900">Véhicules Visiteurs</label>
            </div>
            <div class="flex items-center">
              <input type="checkbox" id="staffVehiclesModule" v-model="editingCompany.modules.staff_vehicles" class="h-4 w-4 text-orange-600 border-gray-300 rounded" />
              <label for="staffVehiclesModule" class="ml-2 block text-sm text-gray-900">Véhicules Staff</label>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-6 flex justify-end gap-3">
        <button
          @click="saveCompany"
          class="px-4 py-2 bg-orange-600 text-white font-semibold rounded-md shadow-sm hover:bg-orange-700"
        >
          {{ editingCompany.id ? 'Sauvegarder les Modifications' : 'Ajouter l\'Entreprise' }}
        </button>
        <button
          v-if="editingCompany.id"
          @click="cancelEdit"
          class="px-4 py-2 bg-gray-300 text-gray-800 font-semibold rounded-md shadow-sm hover:bg-gray-400"
        >
          Annuler
        </button>
      </div>
      <p v-if="errorMessage" class="text-red-600 text-sm mt-4">{{ errorMessage }}</p>
      <p v-if="successMessage" class="text-green-600 text-sm mt-4">{{ successMessage }}</p>
    </div>

    <!-- Liste des entreprises -->
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modules</th>
            <th scope="col" class="relative px-6 py-3">
              <span class="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="company in companies" :key="company.id">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ company.id }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ company.name }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              <span
                v-for="(status, moduleName) in company.modules"
                :key="moduleName"
                :class="[
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                  status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                ]"
                class="mr-2 mb-1"
              >
                {{ moduleName.replace('_', ' ') }}
                <i :class="[status ? 'fas fa-check-circle' : 'fas fa-times-circle']" class="ml-1"></i>
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button @click="editCompany(company)" class="text-orange-600 hover:text-orange-900 mr-4">Éditer</button>
              <button @click="deleteCompany(company.id)" class="text-red-600 hover:text-red-900">Supprimer</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import apiFetch from '@/apiClient';

export default {
  name: 'CompanyManagement',
  setup() {
    const companies = ref([]);
    const editingCompany = ref({
      id: null,
      name: '',
      modules: {
        visitors: false,
        visitor_vehicles: false,
        staff_vehicles: false,
      },
    });
    const errorMessage = ref('');
    const successMessage = ref('');

    if (!localStorage.getItem('token')) {
      errorMessage.value = "Authentification requise.";
    }

    const fetchCompanies = async () => {
      errorMessage.value = '';
      successMessage.value = '';
      try {
        const data = await apiFetch('/companies');
        companies.value = data.map(company => ({
          ...company,
          modules: typeof company.modules === 'string' ? JSON.parse(company.modules) : company.modules || {},
        }));
      } catch (error) {
        errorMessage.value = error.message;
        console.error('Fetch companies error:', error);
      }
    };

    const saveCompany = async () => {
      errorMessage.value = '';
      successMessage.value = '';
      try {
        const method = editingCompany.value.id ? 'PUT' : 'POST';
        const url = editingCompany.value.id 
          ? `/companies/${editingCompany.value.id}` 
          : '/companies';

        await apiFetch(url, {
          method: method,
          body: JSON.stringify({
            name: editingCompany.value.name,
            modules: editingCompany.value.modules,
          }),
        });

        successMessage.value = `Entreprise ${editingCompany.value.id ? 'mise à jour' : 'ajoutée'} avec succès.`;
        resetForm();
        fetchCompanies(); // Actualiser la liste
      } catch (error) {
        errorMessage.value = error.message;
        console.error('Save company error:', error);
      }
    };

    const editCompany = (company) => {
      editingCompany.value = JSON.parse(JSON.stringify(company)); // Deep copy
      // S'assurer que toutes les clés des modules existent
      const defaultModules = { visitors: false, visitor_vehicles: false, staff_vehicles: false };
      editingCompany.value.modules = { ...defaultModules, ...editingCompany.value.modules };
    };

    const cancelEdit = () => {
      resetForm();
    };

    const deleteCompany = async (id) => {
      if (!confirm('Êtes-vous sûr de vouloir supprimer cette entreprise ?')) return;

      errorMessage.value = '';
      successMessage.value = '';
      try {
        await apiFetch(`/companies/${id}`, { method: 'DELETE' });

        successMessage.value = 'Entreprise supprimée avec succès.';
        fetchCompanies(); // Actualiser la liste
      } catch (error) {
        errorMessage.value = error.message;
        console.error('Delete company error:', error);
      }
    };

    const resetForm = () => {
      editingCompany.value = {
        id: null,
        name: '',
        modules: {
          visitors: false,
          visitor_vehicles: false,
          staff_vehicles: false,
        },
      };
    };

    onMounted(fetchCompanies);

    return {
      companies,
      editingCompany,
      errorMessage,
      successMessage,
      saveCompany,
      editCompany,
      cancelEdit,
      deleteCompany,
    };
  },
};
</script>